import { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from 'blaze-ui/Button'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { TopBar } from '../TopBar/TopBar'
import { useStepAction } from '../useStepAction'
import { useVisualStyles } from 'hooks/queries/visualStyles'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import { useUpdateBrandVisualKit } from 'hooks/queries/workspaces/useUpdateBrandVisualKit'
import { useDisplayedFontPairingsAndVisualStyles } from '../BrandVisualStyleSelection/useDisplayedFontPairingsAndVisualStyles'
import { StyleGenerated } from '../BrandVisualStyleSelection/GenerationStage'
import { FontPairing } from 'hooks/queries/fontPairings'
import { BusinessType, VisualStyle } from 'types'
import { V4StyleCard } from './V4StyleCard'
import { BeforeAfterSlider } from './BeforeAfterSlider'
import { getBeforeImage, getAfterImage } from './visualStyleImages'
import { reportError } from 'utils/error'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Images } from 'blaze-ui/icons/24'
import cx from 'classnames'
import Styles from './V4VisualStyleSelection.module.scss'

export interface V4VisualStyleSelectionProps {
  onNext: (selectedVisualStyle: StyleGenerated, fontPairings?: FontPairing[]) => void
  onBack: () => void
  brandKitId?: number
  initialSelectedStyleId?: number
  businessType?: BusinessType
}

const toStyleGenerated = (s: VisualStyle): StyleGenerated => ({ ...s, styleUrl: s.cover_image_url })

export const V4VisualStyleSelection = ({
  onNext,
  onBack,
  brandKitId,
  initialSelectedStyleId,
  businessType,
}: V4VisualStyleSelectionProps) => {
  const { data: visualStyles } = useVisualStyles()
  const {
    displayedVisualStyles: recommendedStyles,
    displayedFontPairings,
    isLoading: isLoadingRecommendations,
    brandKitData,
    brandVisualKitId,
  } = useDisplayedFontPairingsAndVisualStyles(brandKitId)

  const { mutateAsync: updateBrandKit } = useUpdateBrandKit({ id: brandKitId ?? 0 })
  const { mutateAsync: updateBrandVisualKit } = useUpdateBrandVisualKit({ id: brandVisualKitId ?? 0 })

  const allOtherStyles = useMemo(() => {
    if (!visualStyles) return []
    const recommendedIds = new Set(recommendedStyles.map((s) => s.id))
    return visualStyles.filter((s) => !recommendedIds.has(s.id))
  }, [visualStyles, recommendedStyles])

  const [selectedId, setSelectedId] = useState<number | undefined>(initialSelectedStyleId)
  const hasUserSelected = useRef(initialSelectedStyleId != null)

  useEffect(() => {
    if (hasUserSelected.current) return
    if (isLoadingRecommendations) return
    const firstId = recommendedStyles[0]?.id ?? visualStyles?.[0]?.id
    if (firstId != null) {
      setSelectedId(firstId)
      hasUserSelected.current = true
    }
  }, [isLoadingRecommendations, recommendedStyles, visualStyles])

  const handleStyleSelect = (id: number) => {
    hasUserSelected.current = true
    setSelectedId(id)
  }

  const selectedStyle = useMemo(() => {
    if (!selectedId) return undefined
    return [...recommendedStyles, ...allOtherStyles].find((s) => s.id === selectedId)
  }, [selectedId, recommendedStyles, allOtherStyles])

  const { isSaving, run } = useStepAction()
  const { isMobile } = useResponsiveDesign()
  const [showMobileSeeAll, setShowMobileSeeAll] = useState(false)
  const [seeAllSelectedId, setSeeAllSelectedId] = useState<number | null>(null)

  const isNextDisabled = !selectedStyle || isSaving || (!!brandKitId && (isLoadingRecommendations || !brandVisualKitId))

  const handleNext = () => {
    if (!selectedStyle || (brandKitId && !brandVisualKitId)) return
    const generated = toStyleGenerated(selectedStyle)
    run(async () => {
      try {
        if (brandKitId) {
          await Promise.all([
            updateBrandKit({
              content_preferences: {
                ...brandKitData?.brand_kit?.content_preferences,
                apply_visual_style: true,
                image_media_priority: 'only_my_brand_kit',
              },
            }),
            updateBrandVisualKit({ visual_style_id: selectedStyle.id }),
          ])
        }
        onNext(generated, displayedFontPairings)
      } catch (err) {
        reportError(err)
        throw err
      }
    }, 'Something went wrong saving your visual style. Please try again.')
  }

  if (isMobile && showMobileSeeAll) {
    const seeAllStyle = visualStyles?.find((s) => s.id === seeAllSelectedId)

    return (
      <div className={Styles.mobileSeeAllScreen} data-visual-style-page="true">
        <div className={Styles.mobileSeeAllContent}>
          <div className={Styles.mobileSeeAllGrid}>
            {visualStyles?.map((style) => (
              <div
                key={style.id}
                className={cx(Styles.mobileSeeAllCard, {
                  [Styles.selected]: seeAllSelectedId === style.id,
                })}
                onClick={() => setSeeAllSelectedId(style.id)}
              >
                <img src={style.cover_image_url} alt={style.name} />
              </div>
            ))}
          </div>
        </div>
        <div className={Styles.mobileSeeAllBottomCta}>
          <Heading level={2} color="var(--light-100)">
            {seeAllStyle?.name || ''}
          </Heading>
          <Button
            variant="secondary"
            size="lg"
            onPress={() => {
              if (seeAllSelectedId !== null) {
                handleStyleSelect(seeAllSelectedId)
                setShowMobileSeeAll(false)
              }
            }}
            isDisabled={seeAllSelectedId === null}
            className={Styles.mobileSeeAllUseButton}
          >
            Use this style
          </Button>
        </div>
      </div>
    )
  }

  if (isMobile) {
    const mobileDisplayStyles =
      recommendedStyles.length > 0 ? recommendedStyles.slice(0, 3) : (visualStyles ?? []).slice(0, 3)

    return (
      <div className={Styles.mobilePreviewContainer} data-visual-style-page="true">
        <div className={Styles.mobileHeroSection}>
          {selectedStyle?.cover_image_url && (
            <img src={selectedStyle.cover_image_url} alt="" className={Styles.mobileHeroImage} />
          )}
          <div className={Styles.mobileHeroGradient} />
        </div>

        <div className={Styles.mobileThumbnailsSection}>
          <Heading level={2} color="var(--light-100)">
            {selectedStyle?.name}
          </Heading>
          <div className={Styles.mobileThumbnailsContainer}>
            {mobileDisplayStyles.map((style) => (
              <div
                key={style.id}
                className={cx(Styles.mobileThumbnail, { [Styles.selected]: selectedId === style.id })}
                onClick={() => handleStyleSelect(style.id)}
              >
                <img src={style.cover_image_url} alt={style.name} />
              </div>
            ))}
            <button
              className={Styles.mobileSeeAllButton}
              onClick={() => {
                setSeeAllSelectedId(selectedId ?? null)
                setShowMobileSeeAll(true)
              }}
            >
              <Images size={24} color="var(--light-100)" />
              <Text variant="label" color="var(--light-100)">
                See All
              </Text>
            </button>
          </div>

          <Button
            variant="secondary"
            size="lg"
            onPress={handleNext}
            isDisabled={isNextDisabled}
            className={Styles.mobileUseStyleButton}
          >
            {isSaving ? 'Saving...' : 'Use this style'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <MultiStepStepLayout headingLevel={1} scrollContainerClassName={Styles.root} contentClassName={Styles.contentReset}>
      <div className={Styles.page}>
        <TopBar highlightStep="content" />
        <div className={Styles.columns}>
          <div className={Styles.leftCol}>
            <div className={Styles.scrollArea}>
              <div className={Styles.panelHeader}>
                <h1 className={Styles.heading}>Choose a visual style for your content</h1>
                <p className={Styles.subtitle}>
                  Blaze applies a consistent photo treatment — lighting, color, and texture — across everything it
                  creates. You can change this at any time.
                </p>
              </div>

              {recommendedStyles.length > 0 && (
                <section className={Styles.section}>
                  <span className={Styles.sectionLabel}>Recommended for your brand</span>
                  <div className={Styles.cardList}>
                    {recommendedStyles.map((style) => (
                      <V4StyleCard
                        key={style.id}
                        style={style}
                        isSelected={selectedId === style.id}
                        onSelect={handleStyleSelect}
                      />
                    ))}
                  </div>
                </section>
              )}

              {allOtherStyles.length > 0 && (
                <section className={Styles.section}>
                  <span className={Styles.sectionLabel}>All Styles</span>
                  <div className={Styles.cardList}>
                    {allOtherStyles.map((style) => (
                      <V4StyleCard
                        key={style.id}
                        style={style}
                        isSelected={selectedId === style.id}
                        onSelect={handleStyleSelect}
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>

            <div className={Styles.footer}>
              <Button variant="tertiary" size="lg" onPress={onBack}>
                Back
              </Button>
              <Button size="lg" onPress={handleNext} isDisabled={isNextDisabled}>
                {isSaving ? 'Saving...' : `Continue with ${selectedStyle?.name ?? '…'}`}
              </Button>
            </div>
          </div>

          <div className={Styles.rightCol}>
            <BeforeAfterSlider
              beforeImage={getBeforeImage(businessType)}
              afterImage={
                getAfterImage(selectedStyle?.name ?? '', businessType) ?? selectedStyle?.cover_image_url ?? ''
              }
              afterLabel={selectedStyle?.name ?? ''}
            />
          </div>
        </div>
      </div>
    </MultiStepStepLayout>
  )
}
